import { Badge, type BadgeProps } from '@chakra-ui/react';
import { SampleStatus } from '@resistapp/common/types';
import type { ChakraColorScheme } from './env-badge';

export type SampleStatusDisplay = SampleStatus | ' ' | null | undefined;

type SampleStatusBadgeLabelMode = 'compact' | 'full';

export interface SampleStatusBadgeProps extends Omit<BadgeProps, 'colorScheme'> {
  status: SampleStatusDisplay;
  labelMode?: SampleStatusBadgeLabelMode;
}

export function sampleStatusToColorScheme(status: SampleStatusDisplay): ChakraColorScheme {
  switch (status) {
    case SampleStatus.APPROVED:
      return 'green';
    case SampleStatus.APPROVED_WITH_ISSUES:
    case SampleStatus.APPROVED_SKIP_RESULT_STATISTICS:
      return 'orange';
    case SampleStatus.FAILED:
      return 'red';
    case SampleStatus.DUPLICATE:
      return 'purple';
    case SampleStatus.POOLED:
      return 'blue';
    case SampleStatus.SUBMITTED:
    case SampleStatus.UNAPPROVED:
    default:
      return 'gray';
  }
}

export function formatSampleStatusBadgeLabel(
  status: SampleStatusDisplay,
  labelMode: SampleStatusBadgeLabelMode = 'full',
): string {
  if (labelMode === 'compact') {
    switch (status) {
      case SampleStatus.SUBMITTED:
        return 'SUB';
      case SampleStatus.UNAPPROVED:
        return 'NEW';
      case SampleStatus.APPROVED:
        return 'OK';
      case SampleStatus.APPROVED_WITH_ISSUES:
        return 'ISS';
      case SampleStatus.APPROVED_SKIP_RESULT_STATISTICS:
        return 'SKIP';
      case SampleStatus.DUPLICATE:
        return 'DUP';
      case SampleStatus.POOLED:
        return 'POOL';
      case SampleStatus.FAILED:
        return 'FAIL';
      default:
        return '?';
    }
  }

  switch (status) {
    case SampleStatus.SUBMITTED:
      return 'Submitted';
    case SampleStatus.UNAPPROVED:
      return 'Unapproved';
    case SampleStatus.APPROVED:
      return 'Approved';
    case SampleStatus.APPROVED_WITH_ISSUES:
      return 'With issues';
    case SampleStatus.APPROVED_SKIP_RESULT_STATISTICS:
      return 'Skip stats';
    case SampleStatus.DUPLICATE:
      return 'Duplicate';
    case SampleStatus.POOLED:
      return 'Pooled';
    case SampleStatus.FAILED:
      return 'Failed';
    default:
      return 'Mixed';
  }
}

export function SampleStatusBadge({
  status,
  labelMode = 'full',
  fontSize,
  px,
  py,
  ...badgeProps
}: SampleStatusBadgeProps) {
  return (
    <Badge
      colorScheme={sampleStatusToColorScheme(status)}
      borderRadius="full"
      textTransform="none"
      fontWeight="semibold"
      lineHeight="1.2"
      fontSize={fontSize ?? (labelMode === 'compact' ? '10px' : 'xs')}
      px={px ?? (labelMode === 'compact' ? 1.5 : 2)}
      py={py ?? 0.5}
      {...badgeProps}
    >
      {formatSampleStatusBadgeLabel(status, labelMode)}
    </Badge>
  );
}
